import { memo, useCallback, useMemo } from 'react';

import { useTrackSeekedVideo } from '@air/analytics';
import { ChevronLeft, ChevronRight } from '@air/next-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Tooltip } from '@air/primitive-tooltip';

import { PlayerTimeFormat } from '../constants/timeFormat';
import { usePlayerStore } from './PlayerContextProvider';

const PLAYER_TIME_BUTTON_DEFAULT_FPS = 24;

export type PlayerTimeButtonProps = {
  fps?: number;
  seekDirection: 'forward' | 'backward';
  step: number;
};

export const PlayerTimeButton = memo(
  ({ fps = PLAYER_TIME_BUTTON_DEFAULT_FPS, seekDirection, step = 1 }: PlayerTimeButtonProps) => {
    const videoElement = usePlayerStore((state) => state.videoElement);
    const timeFormat = usePlayerStore((state) => state.timeFormat);
    const { trackSeekedVideo } = useTrackSeekedVideo();
    const id = usePlayerStore((state) => state.id);

    const label = useMemo(() => {
      switch (timeFormat) {
        case PlayerTimeFormat.Frames:
          return seekDirection === 'forward' ? 'Seek forward 1 frame' : 'Seek backward 1 frame';
        default:
          return seekDirection === 'forward' ? 'Seek forward 1 second' : 'Seek backward 1 second';
      }
    }, [seekDirection, timeFormat]);

    const handleSeek = useCallback(() => {
      if (!videoElement) return;

      switch (timeFormat) {
        case PlayerTimeFormat.Frames: {
          const framesInSeconds = step * (1 / fps);

          if (seekDirection === 'forward') {
            videoElement.currentTime += framesInSeconds;

            trackSeekedVideo({
              duration: videoElement.currentTime,
              id,
              timeFormat,
              type: 'forward',
            });
          } else if (seekDirection === 'backward') {
            videoElement.currentTime -= framesInSeconds;

            trackSeekedVideo({
              duration: videoElement.currentTime,
              id,
              timeFormat,
              type: 'backward',
            });
          }
          break;
        }
        default:
          if (seekDirection === 'forward') {
            videoElement.currentTime += step;

            trackSeekedVideo({
              duration: videoElement.currentTime,
              id,
              timeFormat,
              type: 'forward',
            });
          } else if (seekDirection === 'backward') {
            videoElement.currentTime -= step;

            trackSeekedVideo({
              duration: videoElement.currentTime,
              id,
              timeFormat,
              type: 'backward',
            });
          }
          break;
      }
    }, [seekDirection, videoElement, fps, step, timeFormat, id, trackSeekedVideo]);

    return (
      <Tooltip label={label}>
        <IconButton
          appearance="ghost"
          color="grey"
          icon={seekDirection === 'forward' ? ChevronRight : ChevronLeft}
          label={label}
          onClick={handleSeek}
          size="small"
        />
      </Tooltip>
    );
  },
);

PlayerTimeButton.displayName = 'PlayerTimeButton';
